import React, { createContext, useContext, useState, useLayoutEffect, useCallback } from 'react';
import { applyCustomizationTheme } from '../utils/Customization';

export type Customization = {
  orgID: string;
  branding: {
    title: string;
    logoUrl?: string;
    headerBgColor: string;
    headerTxtColor: string;
    glow?: {
      color: string;
      intensity: number; // % intensity of glow effect, 0-100
    };
  };
};

type CustomizationContextType = {
  customization: Customization;
  setCustomization: (customization?: Customization) => void;
  // `false` until either a localStorage-cached customization is restored on
  // mount or a real one arrives from the API. The Header reads this to stay
  // invisible during that gap instead of flashing the default green banner.
  isReady: boolean;
};

export const defaultCustomization: Customization = {
  orgID: 'default',
  branding: {
    title: 'AskSmart',
    headerBgColor: '#218838',
    headerTxtColor: '#FFFFFF',
    logoUrl: '',
  },
};

const CACHE_KEY = 'asksmart.customization.v1';

function readCachedCustomization(): Customization | null {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.branding?.headerBgColor || !parsed?.orgID) return null;
    return parsed as Customization;
  } catch {
    return null;
  }
}

function writeCachedCustomization(c: Customization | null): void {
  try {
    if (c) window.localStorage.setItem(CACHE_KEY, JSON.stringify(c));
    else window.localStorage.removeItem(CACHE_KEY);
  } catch {
    // localStorage may throw in private mode / when full — fail silently,
    // the only consequence is the next cold start flashes the default.
  }
}

const CustomizationContext = createContext<CustomizationContextType | undefined>(undefined);

export const CustomizationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Lazy initializer: hydrate from cache synchronously so the first render
  // already has the right colours. Falls back to defaults on cache miss.
  const [customization, setCustomizationState] = useState<Customization>(
    () => readCachedCustomization() ?? defaultCustomization,
  );
  const [isReady, setIsReady] = useState<boolean>(() => readCachedCustomization() !== null);

  const setCustomization = useCallback((next?: Customization) => {
    if (!next) {
      // Reset path (logout / leaving protected route). Clear the cache so the
      // next user on this device doesn't briefly see a previous tenant's
      // branding before their own session resolves.
      setCustomizationState(defaultCustomization);
      writeCachedCustomization(null);
      setIsReady(false);
      return;
    }
    setCustomizationState(next);
    // Only persist when this is a real, saved org customization — not the
    // live preview RegistrationPage drives during the Customization stage (orgID === '') or
    // the synthetic default placeholder (orgID === 'default').
    const isRealOrg = !!next.orgID && next.orgID !== 'default';
    if (isRealOrg) {
      writeCachedCustomization(next);
    }
    setIsReady(true);
  }, []);

  // useLayoutEffect (not useEffect) so the CSS vars are written before the
  // browser paints — eliminates the one-frame flash even on cache hit.
  useLayoutEffect(() => {
    applyCustomizationTheme(customization);
  }, [customization]);

  return (
    <CustomizationContext.Provider value={{ customization, setCustomization, isReady }}>
      {children}
    </CustomizationContext.Provider>
  );
};

export const useCustomization = () => {
  const ctx = useContext(CustomizationContext);
  if (!ctx) throw new Error("useCustomization must be used within a CustomizationProvider");
  return ctx;
};
